import { APP_SETTINGS } from '../config/appSettings'

export type SyncDomain = 'notes' | 'settings' | 'assets'

export interface SyncRequest {
  push: boolean
  domains: SyncDomain[]
  /** Explicit user retry or a network-online transition, never a periodic wake. */
  retryNow?: boolean
}

export interface SyncCycleResult {
  noteIds: string[]
  removedNoteIds: string[]
  settingKeys: string[]
  assetIds: string[]
}

export interface NextSyncAttempt {
  at: number
  request: SyncRequest
}

interface SyncRuntimeOptions {
  execute: (request: SyncRequest) => Promise<SyncCycleResult>
  nextAttempt: () => Promise<NextSyncAttempt | null>
  onResult?: (result: SyncCycleResult) => void
  onError?: (error: unknown) => void
  isOnline?: () => boolean
  isVisible?: () => boolean
  /** A backend with no change hints (Neon): this is the only safety poll interval, whatever the hint channel reports. */
  fixedSafetyPollMs?: number
  now?: () => number
  random?: () => number
}

const ALL_DOMAINS: SyncDomain[] = ['notes', 'settings', 'assets']

function mergeRequests(left: SyncRequest | null, right: SyncRequest): SyncRequest {
  return {
    push: Boolean(left?.push || right.push),
    domains: [...new Set([...(left?.domains ?? []), ...right.domains])],
  }
}

export class SyncRuntime {
  private pending: SyncRequest | null = null
  private runPromise: Promise<void> | null = null
  private requestTimer: number | null = null
  private requestAt = Number.POSITIVE_INFINITY
  private safetyTimer: number | null = null
  private retryTimer: number | null = null
  private realtimeConnected = false
  private consecutiveFailures = 0
  private failureRetryAt = 0
  private disposed = false
  private readonly isOnline: () => boolean
  private readonly isVisible: () => boolean
  private readonly now: () => number
  private readonly random: () => number

  constructor(private readonly options: SyncRuntimeOptions) {
    this.isOnline = options.isOnline ?? (() => navigator.onLine)
    this.isVisible = options.isVisible ?? (() => document.visibilityState === 'visible')
    this.now = options.now ?? Date.now
    this.random = options.random ?? Math.random
  }

  start(): void {
    this.request({ push: true, domains: ALL_DOMAINS }, 0)
    this.scheduleSafetyPoll()
  }

  request(request: SyncRequest, delayMs = APP_SETTINGS.sync.pushDebounceMs): void {
    if (this.disposed) return
    if (request.retryNow) this.failureRetryAt = 0
    this.pending = mergeRequests(this.pending, request)
    const requestedAt = this.now() + Math.max(0, delayMs)
    if (requestedAt <= this.now() && this.requestTimer === null) {
      void this.drain()
      return
    }
    if (this.requestTimer !== null && requestedAt >= this.requestAt) return
    if (this.requestTimer !== null) globalThis.clearTimeout(this.requestTimer)
    this.requestAt = requestedAt
    this.requestTimer = globalThis.setTimeout(() => {
      this.requestTimer = null
      this.requestAt = Number.POSITIVE_INFINITY
      void this.drain()
    }, Math.max(0, requestedAt - this.now()))
  }

  setRealtimeConnected(connected: boolean): void {
    if (connected) {
      // Subscribe first, then catch up. This closes the gap in which a database
      // change can commit after the startup pull but before the channel joins.
      this.request({ push: false, domains: ALL_DOMAINS }, 0)
    }
    if (this.realtimeConnected === connected) return
    this.realtimeConnected = connected
    this.scheduleSafetyPoll()
  }

  dispose(): void {
    this.disposed = true
    if (this.requestTimer !== null) globalThis.clearTimeout(this.requestTimer)
    if (this.safetyTimer !== null) globalThis.clearTimeout(this.safetyTimer)
    if (this.retryTimer !== null) globalThis.clearTimeout(this.retryTimer)
  }

  private async drain(): Promise<void> {
    if (this.disposed || this.runPromise) return
    if (!this.pending || !this.isOnline() || !this.isVisible()) return
    // Every wake source shares the failure deadline, including safety polls,
    // realtime invalidations and local writes. Keep merging work while waiting.
    if (this.now() < this.failureRetryAt) return
    if (this.retryTimer !== null) {
      globalThis.clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    const request = this.pending
    this.pending = null
    this.runPromise = this.options.execute(request)
      .then((result) => {
        this.consecutiveFailures = 0
        this.failureRetryAt = 0
        this.options.onResult?.(result)
      })
      .catch((error: unknown) => {
        this.consecutiveFailures += 1
        this.failureRetryAt = this.now() + Math.min(
          APP_SETTINGS.sync.retryMaxMs,
          APP_SETTINGS.sync.retryBaseMs
            * 2 ** Math.min(this.consecutiveFailures - 1, APP_SETTINGS.sync.retryExponentCap),
        )
        this.pending = mergeRequests(this.pending, request)
        this.options.onError?.(error)
      })
      .finally(async () => {
        this.runPromise = null
        const shouldDrainPending = this.consecutiveFailures === 0 && this.pending !== null
        await this.scheduleRetry()
        this.scheduleSafetyPoll()
        if (shouldDrainPending && this.pending) this.request(this.pending, 0)
      })
    return this.runPromise
  }

  private scheduleSafetyPoll(): void {
    if (this.disposed) return
    if (this.safetyTimer !== null) globalThis.clearTimeout(this.safetyTimer)
    const base = this.options.fixedSafetyPollMs ?? (this.realtimeConnected
      ? APP_SETTINGS.sync.healthySafetyPollMs
      : APP_SETTINGS.sync.disconnectedSafetyPollMs)
    const spread = base * APP_SETTINGS.sync.pollJitterRatio
    const delay = Math.max(1, Math.round(base - spread + this.random() * spread * 2))
    this.safetyTimer = globalThis.setTimeout(() => {
      this.safetyTimer = null
      this.request({ push: true, domains: ALL_DOMAINS }, 0)
      this.scheduleSafetyPoll()
    }, delay)
  }

  private async scheduleRetry(): Promise<void> {
    if (this.disposed) return
    if (this.retryTimer !== null) globalThis.clearTimeout(this.retryTimer)
    this.retryTimer = null
    const next = await this.options.nextAttempt()
    if (this.disposed) return
    if (next) this.pending = mergeRequests(this.pending, next.request)
    // An overdue outbox item must not create a zero-delay loop after failure.
    const retryAt = this.consecutiveFailures > 0
      ? this.failureRetryAt
      : next?.at ?? Number.POSITIVE_INFINITY
    if (!Number.isFinite(retryAt) || !this.pending) return
    this.retryTimer = globalThis.setTimeout(() => {
      this.retryTimer = null
      if (this.pending) this.request(this.pending, 0)
    }, Math.max(0, retryAt - this.now()))
  }
}

export function emptySyncCycleResult(): SyncCycleResult {
  return {
    noteIds: [],
    removedNoteIds: [],
    settingKeys: [],
    assetIds: [],
  }
}
